import { AUTH_ROUTES, getMeOutput, } from "@repo/contract/iam";
import { ApiError, SessionExpiredError } from "./api-error";
import { readJson, refreshTokens, requestOtp, signout, verifyOtp, } from "./auth-api";
import { toTokenPair } from "./token-pair";
/**
 * ── THE ONE OBJECT ALLOWED TO SPEND A REFRESH TOKEN. ─────────────────────────
 *
 * The refresh token is opaque, single-use and rotating (D15), and presenting a
 * consumed one is treated as THEFT: the server revokes the entire token family,
 * every token sharing that session id. So the failure this file exists to
 * prevent is not "a refresh failed". It is:
 *
 *   1. a screen mounts and fires three requests in parallel;
 *   2. the access token has expired, so all three come back 401 at once;
 *   3. three refreshes start in parallel holding the SAME refresh token;
 *   4. one wins; the other two are reuse, and the family is revoked;
 *   5. the user is signed out of every device, at random, for no visible
 *      reason, and it reads exactly like a server bug.
 *
 * The defence is one line — {@link ApiClient.refresh} — and it is a
 * **correctness requirement, not an optimisation**. Note what it is not: not a
 * mutex and not a queue. The second caller does not want to refresh again, it
 * wants the answer the first caller is already fetching, so what is shared is a
 * PROMISE. A queue would serialise two presentations of a single-use token,
 * which is precisely the thing being prevented.
 *
 * `refreshCount` exists so a test can assert the network was hit EXACTLY once
 * with ten callers waiting. Nothing in the app branches on it.
 *
 * ── THE PROMISE IS PER TAB; THE STORAGE IS NOT. ─────────────────────────────
 * On the web the pair lives in `localStorage`, which every tab shares, while
 * the shared promise above lives in one tab's memory. Two tabs can therefore
 * present the same refresh token at once, and inside the server's overlap
 * window that is not a harmless 409: one tab receives the successor, the other
 * a sibling that invalidates it, and whichever writes storage last decides
 * whether the stored token is the live one or the dead one. So the network
 * half of a refresh runs under a Web Lock, and the first thing it does inside
 * the lock is READ STORAGE AGAIN: a tab that waited while another refreshed
 * finds a different refresh token there and adopts that pair without a
 * request. A lock without the re-read would only serialise two spends of the
 * same token. The lock is NOT the in-tab layer — ten callers in one tab still
 * share one promise and take the lock once — and where there is no
 * `navigator.locks` (a WebView that lacks it, a test) nothing changes.
 *
 * ── WHAT SIGN-OUT CAN AND CANNOT DO (D15, WITH RECORDED DISSENT). ────────────
 * Sign-out revokes the session, which kills renewal immediately. An
 * already-issued access token is validated by signature alone with no database
 * read, so it keeps working until its `exp` — at most 15 minutes. That is D15's
 * accepted cost. No screen in this app may promise instant global sign-out, and
 * this is not to be "fixed" by adding a point read.
 */
/**
 * Refresh this many seconds before `exp` rather than waiting for a 401.
 *
 * 60s against the 900s access-token life, so it costs at most one extra refresh
 * per 15 minutes and takes the reactive-401 path out of ordinary use. It also
 * absorbs clock skew: a phone whose clock runs a few seconds fast would
 * otherwise believe a token is live that the server has already expired.
 */
export const REFRESH_SKEW_SECONDS = 60;
/**
 * One name for every tab and every build. Two app versions open side by side
 * — a tab that has not reloaded since a deploy — must still exclude each
 * other, so this string is never versioned or renamed.
 */
const REFRESH_LOCK_NAME = "mypocketgym:auth-refresh";
export class ApiClient {
    /** Observable only so a test can prove the single-in-flight rule. */
    refreshCount = 0;
    locks;
    options;
    /** The shared promise. Non-null exactly while a refresh is on the wire. */
    inFlightRefresh = null;
    constructor(options) {
        this.options = options;
        this.locks =
            options.locks === undefined
                ? (globalThis.navigator
                    ?.locks ?? null)
                : options.locks;
    }
    // ── Sign-in ───────────────────────────────────────────────────────────────
    /**
     * See `requestOtp`: this cannot tell you whether the address is known.
     *
     * `locale` is the language the sign-in email is written in. It is the app's
     * own setting that belongs here, never the device's — see `sign-in.tsx`.
     */
    requestCode(identifier, locale) {
        return requestOtp(this.fetcher, identifier, locale);
    }
    /** `ref`: the share link this device arrived through, if any (`D260`). */
    async submitCode(verificationId, code, ref) {
        const verified = await verifyOtp(this.fetcher, verificationId, code, ref);
        await this.options.store.write(toTokenPair(verified));
        return {
            accountId: verified.accountId,
            isNewAccount: verified.isNewAccount,
        };
    }
    // ── Session ───────────────────────────────────────────────────────────────
    /** Whether there is a stored pair at all. Says nothing about whether it works. */
    async hasSession() {
        return (await this.options.store.read()) !== null;
    }
    /**
     * `GET /auth/me` is what proves a stored pair is still a session, so it goes
     * through the ordinary authorized path — proactive refresh, 401 retry and
     * all — rather than being sent with a raw token.
     */
    async me() {
        return readJson(await this.authorizedFetch(AUTH_ROUTES.me), getMeOutput);
    }
    /**
     * **Calls the server.** Clearing storage alone would leave the session and its
     * whole refresh family alive: the user would see "signed out" while anything
     * still holding the refresh token could mint access tokens for another 90
     * days. That is the opposite of what the button means.
     *
     * The local clear happens regardless of the outcome. If the call fails there
     * is nothing more this client can do, and leaving a credential on the device
     * after the user asked to sign out is the worse of the two failures.
     *
     * ── THIS IS THE DOOR WHERE THE CREDENTIAL IS STILL GOOD. ────────────────
     * Anything that needs an authenticated call ON THE WAY OUT has to happen
     * BEFORE this method, not inside `onSessionEnded` — by then `endSession()`
     * has cleared the store and every later request is a 401. The push token is
     * the case that exists today: `AuthProvider.signOut` de-registers the device
     * first and only then calls this. `endSession()` below is the other door and
     * is also reached from a failed refresh, where there is no credential left at
     * all — it may only clear local state.
     */
    async signOut() {
        try {
            const pair = await this.freshTokens();
            await signout(this.fetcher, pair.accessToken);
        }
        catch {
            // Already expired, already revoked, or offline. Fall through to the
            // local clear rather than surfacing a failure the user cannot act on.
        }
        finally {
            await this.endSession();
        }
    }
    /**
     * **Does not call the server.** The third door, and the only one a caller
     * outside this class may open: the session is over because the SERVER
     * ended it — `DELETE /auth/account` (`D153`) has already banned the account
     * and revoked the whole refresh family — so there is nothing left to say
     * goodbye to. `signOut()` above would do the wrong thing here twice over:
     * `freshTokens()` may present the revoked refresh token to get a fresh
     * access token (the one presentation that reads as reuse), and the
     * `/auth/signout` it then sends is a request about a session that no
     * longer exists.
     *
     * It is not `endSession()` made public. That method is also the expiry
     * door, reached from a failed refresh, and a name that says "end" invites
     * a screen to call it in place of `signOut()` — which would clear the
     * device and leave the family alive for 90 days. "Discard" says what this
     * does and what it does not: the credential is dropped, and nothing about
     * the server's state is changed, because the caller has already proved the
     * server changed it. `onSessionEnded` fires as on every path, so the push
     * token and the app's status follow.
     */
    async discardSession() {
        await this.endSession();
    }
    // ── Authorized requests ───────────────────────────────────────────────────
    /**
     * `fetch` with a live access token attached, refreshing when necessary and
     * retrying **once** on a 401.
     *
     * `init.body` must be replayable — a string, or absent. The retry sends the
     * request a second time and a `ReadableStream` body cannot be consumed twice;
     * every caller in this app sends JSON strings.
     */
    authorizedFetch = async (path, init) => {
        const pair = await this.freshTokens();
        const first = await this.send(path, pair.accessToken, init);
        if (first.status !== 401) {
            return first;
        }
        // The server rejected a token this client believed was live: clock skew
        // wider than the margin, or a token issued before a config change.
        const current = await this.options.store.read();
        if (current !== null && current.accessToken !== pair.accessToken) {
            // Somebody refreshed while this request was on the wire. Retry with what
            // they got. Starting a second refresh here — with a token that holder has
            // already spent — is exactly the reuse that revokes the family.
            return await this.send(path, current.accessToken, init);
        }
        const renewed = await this.refresh();
        return await this.send(path, renewed.accessToken, init);
    };
    /**
     * Refresh now if the stored access token is near its end, and say nothing
     * either way (`D194`). The app calls this when it comes back to the
     * foreground, BEFORE its screens refetch: after a break longer than the
     * token's fifteen minutes the first read would otherwise wait for the
     * refresh it triggers. It joins the single in-flight refresh like every
     * other caller, so it can never spend the refresh token twice; no session,
     * offline or a refused refresh are all left to the next real request.
     */
    async warmTokens() {
        await this.freshTokens().catch(() => undefined);
    }
    // ── The refresh ───────────────────────────────────────────────────────────
    /**
     * **The single in-flight promise.** Concurrent callers get this one; they do
     * not start their own. That is the entire defence against family revocation.
     *
     * `runRefresh()` is invoked before the assignment but SYNCHRONOUSLY with it,
     * so there is no tick in which a second caller can observe a null field for a
     * refresh that has already begun. Anything `await`ed before this assignment
     * would reopen the race this method closes.
     */
    refresh() {
        this.inFlightRefresh ??= this.runRefresh().finally(() => {
            this.inFlightRefresh = null;
        });
        return this.inFlightRefresh;
    }
    async runRefresh() {
        // Re-read rather than trusting a field. Storage is the source of truth for
        // a single-use credential; an in-memory copy goes stale the instant a
        // rotation lands, and presenting the stale one is the reuse being avoided.
        const stored = await this.options.store.read();
        if (stored === null) {
            await this.endSession();
            throw new SessionExpiredError();
        }
        if (this.locks === null) {
            return await this.spend(stored);
        }
        return await this.locks.request(REFRESH_LOCK_NAME, async () => {
            // The whole point of the lock. Another tab may have held it while this
            // one waited, and spent `stored` on the way: the pair in storage now is
            // its answer, and presenting `stored` again is the reuse. Compared on
            // the refresh token, never on expiry — a 401 retry arrives here holding
            // a token that looks live and was rejected anyway, and must refresh.
            const current = await this.options.store.read();
            if (current === null) {
                // Another tab signed out, or its refresh was refused.
                await this.endSession();
                throw new SessionExpiredError();
            }
            if (current.refreshToken !== stored.refreshToken) {
                return current;
            }
            return await this.spend(current);
        });
    }
    /** Presents `pair`'s refresh token: the one network refresh. */
    async spend(pair) {
        this.refreshCount += 1;
        try {
            const renewed = toTokenPair(await refreshTokens(this.fetcher, pair.refreshToken));
            await this.options.store.write(renewed);
            return renewed;
        }
        catch (cause) {
            if (cause instanceof ApiError && cause.status === 401) {
                // Expired, revoked, or a family that reuse detection has already
                // killed. There is no token left this client may safely present.
                await this.endSession();
                throw new SessionExpiredError(undefined, { cause });
            }
            // Offline, a 5xx, a captive portal. The session may well still be alive,
            // so the credential stays on the device and the caller sees the real
            // failure. Clearing here would sign out every user who rode a lift.
            throw cause;
        }
    }
    /** The stored pair if it has life left in it, refreshed otherwise. */
    async freshTokens() {
        const stored = await this.options.store.read();
        if (stored === null) {
            throw new SessionExpiredError();
        }
        const nowSeconds = Math.floor(this.now() / 1000);
        if (stored.expiresAt - nowSeconds > REFRESH_SKEW_SECONDS) {
            return stored;
        }
        return await this.refresh();
    }
    // ── Internals ─────────────────────────────────────────────────────────────
    /**
     * Base URL plus path, resolved per call. The base is already normalized with
     * no trailing slash, so this cannot produce the `//auth/...` that a Worker
     * routes as a different path and answers with a 404 reading as a missing
     * route.
     */
    fetcher = async (path, init) => {
        const base = await this.options.baseUrl();
        const doFetch = this.options.fetch ?? globalThis.fetch;
        return await doFetch(`${base}${path}`, {
            ...init,
            headers: {
                // Stated rather than assumed: every caller here parses the reply as
                // JSON, so every one of them says so. First in the literal, so a caller
                // that genuinely wants something else can override it.
                accept: "application/json",
                ...init?.headers,
            },
        });
    };
    send(path, accessToken, init) {
        return this.fetcher(path, {
            ...init,
            headers: {
                ...init?.headers,
                // LAST in the literal, for the opposite reason `accept` is first: the
                // credential is not the caller's to weaken.
                authorization: `Bearer ${accessToken}`,
            },
        });
    }
    now() {
        return this.options.now?.() ?? Date.now();
    }
    /**
     * The one place a live session becomes a dead one — reached both from a
     * deliberate `signOut()` and from a refresh that came back 401.
     *
     * `onSessionEnded` therefore runs on BOTH paths and cannot assume a working
     * credential: it is for clearing local state only. See `signOut()` above for
     * where an authenticated goodbye belongs.
     */
    async endSession() {
        await this.options.store.clear();
        this.options.onSessionEnded?.();
    }
}
//# sourceMappingURL=api-client.js.map